import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import Input from "components/form/Input";
import { CustomSelect } from "ui/Input/Select";
import styles from "../Calendar.module.css";
import { useDispatch } from "react-redux";
import { getCalendarEquipment, getCalendarEquipmentCategories } from "store/calendar";

const getCount = (val) => {
  if (Array.isArray(val)) return val.length
  return val ? 1 : 0
}

const normalizeMultiValue = (val) => {
  if (Array.isArray(val)) return val
  return val ? [val] : []
}

/** Not a real item: it belongs to no category, so no category filter may remove it. */
const UNASSIGNED_OPTION = { label: "Unassigned", value: "unassigned" }

const filterEquipment = (equipment, category, search) =>
  equipment.filter(
    (eq) =>
      (!category?.length || category.includes(eq.category)) &&
      (!search || eq.label.toLowerCase().includes(search.toLowerCase()))
  )

const EquipmentFilters = ({ value = {}, onChange, onOptionsChange, hideSearch = false, fullWidth = false, selectProps = {} }) => {
  const dispatch = useDispatch();
  const [categories, setCategories] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    Promise.all([
      dispatch(getCalendarEquipmentCategories()),
      dispatch(getCalendarEquipment()),
    ])
      .then(([catRes, eqRes]) => {
        const catOptions = (catRes.data || []).map((cat) => ({ label: cat.name, value: cat.id }));
        const eqOptions = (eqRes.data || []).map((eq) => ({ label: eq.equipment_title, value: eq.id, category: eq.category?.id }));
        setCategories(catOptions);
        setEquipment(eqOptions);
        if (onOptionsChange) {
          onOptionsChange({ equipmentOptions: eqOptions, categoryOptions: catOptions });
        }
      })
      .catch(() => { });
  }, []);

  // Filter equipment by search and selected category
  const filteredEquipment = filterEquipment(equipment, value.category, search);
  const filteredEquipmentWithUnassigned = [UNASSIGNED_OPTION, ...filteredEquipment]

  // Overlay logic for Figma-style count display
  const equipmentSelectRef = useRef(null);
  const categorySelectRef = useRef(null);
  const equipmentCount = getCount(value.equipment)
  const categoryCount = getCount(value.category)
  const normalizedEquipment = normalizeMultiValue(value.equipment)
  const normalizedCategory = normalizeMultiValue(value.category)

  /**
   * Narrowing the categories drops any equipment that no longer belongs to one.
   *
   * Without this the calendar keeps filtering on a piece of equipment the guide can no longer see
   * in the list — it is gone from the dropdown but still in the value, so the grid stays narrowed
   * with nothing on screen to explain why. "Unassigned" is not a real item and belongs to no
   * category, so it survives.
   */
  const handleCategoryChange = (categoryIds) => {
    const allowed = new Set(filterEquipment(equipment, categoryIds, "").map((item) => item.value))
    onChange({
      ...value,
      category: categoryIds,
      equipment: normalizedEquipment.filter((id) => id === UNASSIGNED_OPTION.value || allowed.has(id))
    })
  }

  return (
    <div className={`${styles.calendarFilters} ${fullWidth ? styles.calendarFiltersFull : ''}`}>
      {!hideSearch && (
        <Input
          placeholder="Search for Crew, Equipment"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={`${styles.calendarSearch} ${fullWidth ? styles.filterSearchFull : ''}`}
        />
      )}
      {/* Category first: it narrows the equipment list, so choosing it the other way round means
          picking from a list that is about to change under you. */}
      <div className={fullWidth ? styles.filterSelectWrapperFull : styles.filterSelectWrapper}>
        <CustomSelect
          ref={categorySelectRef}
          mode="multiple"
          styles={{ width: "100%" }}
          placeholder="Categories"
          value={normalizedCategory}
          onChange={handleCategoryChange}
          options={categories}
          className={styles.calendarFilterSelect}
          allowClear
          maxTagCount={0}
          showSearch={false}
          tagRender={() => null}
          {...selectProps}
        />
        {categoryCount > 0 && (
          <div className={styles.filterCountOverlay}>
            {`Categories (${categoryCount})`}
          </div>
        )}
      </div>
      <div className={fullWidth ? styles.filterSelectWrapperFull : styles.filterSelectWrapper}>
        <CustomSelect
          ref={equipmentSelectRef}
          mode="multiple"
          styles={{ width: "100%" }}
          placeholder="Equipment"
          value={normalizedEquipment}
          onChange={(val) => onChange({ ...value, equipment: val })}
          options={filteredEquipmentWithUnassigned}
          className={styles.calendarFilterSelect}
          allowClear
          maxTagCount={0}
          showSearch={false}
          tagRender={() => null}
          {...selectProps}
        />
        {equipmentCount > 0 && (
          <div className={styles.filterCountOverlay}>
            {`Equipment (${equipmentCount})`}
          </div>
        )}
      </div>
    </div>
  );
};

EquipmentFilters.propTypes = {
  value: PropTypes.shape({
    equipment: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number]),
    category: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.number])
  }),
  onChange: PropTypes.func,
  onOptionsChange: PropTypes.func,
  hideSearch: PropTypes.bool,
  fullWidth: PropTypes.bool,
  selectProps: PropTypes.object
}

export default EquipmentFilters;
